import YoutubeIcon from '@assets/icons/youtube.svg?react';
import { Box, Dialog, DialogContent, IconButton } from '@mui/material';
import { Node, NodeViewWrapper, ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react';
import { getYoutubeId } from '@utils/common';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

const MAX_WIDTH = 550;
const MIN_WIDTH = 200;

type CustomIFrameAttributes = React.IframeHTMLAttributes<HTMLIFrameElement> & {
    textAlign?: string;
};

type ExternalThumbnailViewProps = ReactNodeViewProps<HTMLIFrameElement> & {
    node: ReactNodeViewProps['node'] & {
        attrs: CustomIFrameAttributes;
    };
};

const getVideoDetails = (src: string) => {
    const videoId = getYoutubeId(src);
    if (videoId) {
        return {
            src: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
            thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        };
    }

    return { src, thumbnail: null };
};

const ExternalThumbnailView = (props: ExternalThumbnailViewProps) => {
    const [open, setOpen] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const textAlign = props.node.attrs.textAlign || 'center';
    const width = Math.min(Math.max(Number(props.node.attrs.width), MIN_WIDTH), MAX_WIDTH);

    const videoDetails = getVideoDetails(props.node.attrs.src ?? '');

    // Mouse event handlers
    const onMouseDown = (e: React.MouseEvent) => {
        setIsResizing(true);
        e.stopPropagation();
        e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
        if (!isResizing || !containerRef.current) return;
        const newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;

        props.updateAttributes({ width: Math.min(Math.max(newWidth, MIN_WIDTH), MAX_WIDTH) });
    };

    const onMouseUp = () => setIsResizing(false);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        } else {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isResizing]);

    return (
        <NodeViewWrapper className={clsx({ 'ProseMirror-selectednode': props.selected })}>
            <div
                ref={containerRef}
                className={clsx('relative w-fit', {
                    'mx-auto': textAlign === 'center',
                    'ml-0 mr-auto': textAlign === 'left',
                    'ml-auto mr-0': textAlign === 'right',
                })}
                contentEditable={false}
                draggable="true"
                data-drag-handle
            >
                <Box
                    component="img"
                    sx={{
                        outline: (theme) =>
                            props.selected && props.editor.isEditable
                                ? `3px solid ${theme.palette.primary.main}`
                                : 'none',
                        width,
                        minHeight: videoDetails.thumbnail ? 0 : 200,
                    }}
                    src={videoDetails.thumbnail || ''}
                    alt="Video Thumbnail"
                    className="max-w-full"
                />
                <IconButton
                    className="p-[3%] w-fit absolute top-1/2 left-1/2 right-1/2 -translate-y-1/2 -translate-x-1/2"
                    onClick={() => setOpen(true)}
                >
                    <YoutubeIcon style={{ width: width / 4, height: width / 4 }} />
                </IconButton>
                <div
                    className={clsx('absolute right-0 bottom-0 w-3 h-3 bg-primary-600 cursor-nwse-resize z-10', {
                        hidden: !props.editor.isEditable || !props.selected,
                    })}
                    onMouseDown={onMouseDown}
                />
                <Dialog
                    open={open}
                    onClose={() => setOpen(false)}
                    sx={{
                        '& .MuiDialog-paper': {
                            minWidth: {
                                md: '60%',
                                xs: '90%',
                            },
                        },
                    }}
                >
                    <DialogContent
                        sx={{
                            p: {
                                md: 4,
                                xs: 0,
                            },
                            height: {
                                md: '50vh',
                                xs: '75vh',
                            },
                            width: '100%',
                        }}
                    >
                        <iframe
                            src={videoDetails.src}
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            title="Video"
                            width="100%"
                            style={{ height: '100%', border: 0 }}
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                    </DialogContent>
                </Dialog>
            </div>
        </NodeViewWrapper>
    );
};

type CustomExtensionOptions = {
    customOption: string;
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        externalVideo: {
            setExternalVideo: (options: CustomIFrameAttributes) => ReturnType;
        };
    }

    interface ExtensionOptions {
        customOption: CustomExtensionOptions;
    }
}

const ExternalVideo = Node.create({
    name: 'externalVideo',
    group: 'block',
    draggable: true,
    addAttributes() {
        return {
            src: { default: null },
            width: { default: null },
            height: { default: null },
        };
    },
    parseHTML() {
        return [
            {
                tag: 'iframe[src]',
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['iframe', { ...HTMLAttributes, frameborder: '0', allowfullscreen: 'true' }];
    },
    addNodeView(this) {
        return ReactNodeViewRenderer(ExternalThumbnailView);
    },
    addCommands() {
        return {
            setExternalVideo:
                (options) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: options,
                    });
                },
        };
    },
});

export { ExternalVideo };
